import { Container } from '@/components/Container/Container';
import { IconHome } from '@/components/Icons/IconHome';
import { NextPage } from 'next';
import { ArrowIcon, Breadcrumbs, Button } from '@netfront/ui-library';
import Link from 'next/link';
import { PublicDashboardPage } from '@/components/Page/PublicDashboardPage';
import useGetPublicExercises from '@/hooks/useGetPublicExercises/useGetPublicExercises';
import { ModuleCard } from '@/components/ModuleCard/ModuleCard';
import { ExerciseGroup } from '@/components/ExerciseGroup/ExerciseGroup';
import { useCaptureReturnToModule } from '@/hooks/useCaptureReturnToModule/useCaptureReturnToModule';
import { cn } from '@/utils';
import { PublicActivity } from '@/hooks/useGetPublicExercises/useGetPublicExercises';
import { useRouter } from 'next/router';
import useGetTemporarySession from '@/hooks/useGetTemporarySession/useGetTemporarySession';
import { useState } from 'react';
import { isContentGroupVisible } from '@/utils/tags';

function IconGrid() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
      <rect x="0" y="0" width="7" height="7" rx="1" />
      <rect x="11" y="0" width="7" height="7" rx="1" />
      <rect x="0" y="11" width="7" height="7" rx="1" />
      <rect x="11" y="11" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconList() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
      <rect x="0" y="2" width="18" height="2" rx="1" />
      <rect x="0" y="8" width="18" height="2" rx="1" />
      <rect x="0" y="14" width="18" height="2" rx="1" />
    </svg>
  );
}

const FILTER_OPTIONS = [
  { label: 'All', slug: 'all' },
  { label: 'In the moment', slug: 'in-the-moment' },
  { label: 'Reflect and explore', slug: 'reflect-and-explore' },
  { label: 'Plan and take action', slug: 'plan-and-take-action' },
];

function toSlug(str: string) {
  return str.toLowerCase().replace(/\s+/g, '-');
}

const ExerciseToolboxNextPage: NextPage = () => {
  const { data } = useGetPublicExercises();
  const { returnToModuleUrl } = useCaptureReturnToModule();
  const router = useRouter();
  const activeFilter = (router.query.filter as string) ?? 'all';
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('list');
  useGetTemporarySession();

  function handleFilterClick(slug: string) {
    router.push({ pathname: router.pathname, query: slug === 'all' ? {} : { filter: slug } }, undefined, { shallow: true });
  }

  function renderFilterTags() {
    return (
      <div className="flex flex-col gap-2">
        <h4 className="h5 mt-2">Filter exercises</h4>
        <ul className="list-style-none m-0 flex flex-col gap-1">
          {FILTER_OPTIONS.map(({ label, slug }) => {
            const isActive = activeFilter === slug;
            return (
              <li key={slug} className="m-0">
                <button
                  onClick={() => handleFilterClick(slug)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded text-sm font-medium transition-200',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-transparent text-surface-foreground hover:bg-primary/10',
                  )}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  function renderExercises() {
    if (!data) {
      return (
        <ul className="flex flex-wrap -mx-2 list-style-none">
          {/* Fixed-count static placeholder list, never reordered — index key is safe here. NOSONAR */}
          {Array.from({ length: 12 }).map((_, index) => (
            <li key={`skelton-${index}`} className="w-full md:w-1/2 lg:w-1/4 p-2 m-0">
              <ModuleCard type="ExerciseToolbox" isLoading={true} />
            </li>
          ))}
        </ul>
      );
    }

    const published = data.contentGroup.searchContentGroups.filter((activity) => isContentGroupVisible(activity));

    // Build ordered map of tag id -> { tag, exercises }
    const groupMap = new Map<number | 'untagged', { label: string; sort: number; exercises: PublicActivity[] }>();

    published.forEach((activity) => {
      const tag = activity.tags.find(({ name }) => name !== 'Coming Soon');
      const key = tag ? tag.id : 'untagged';

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          label: tag ? tag.name : 'Other',
          sort: tag ? tag.sort : Infinity,
          exercises: [],
        });
      }

      groupMap.get(key)!.exercises.push(activity);
    });

    const groups = Array.from(groupMap.values())
      .toSorted((a, b) => a.sort - b.sort)
      .map((group) => ({
        ...group,
        exercises: group.exercises.toSorted((a, b) => a.sort - b.sort),
      }))
      .filter(({ label }) => activeFilter === 'all' || toSlug(label) === activeFilter);

    return groups.map(({ label, exercises }) => (
      <ExerciseGroup key={label} title={label} exercises={exercises} viewMode={viewMode} />
    ));
  }

  return (
    <PublicDashboardPage
      meta={{
        seoTitle: 'Exercises',
      }}
      title="Exercises"
      sideMenuChildren={renderFilterTags()}
    >
      <Container additionalClassNames="py-8 md:py-12">
        <Breadcrumbs
          items={[
            {
              content: (
                <Link className="w-4" href="/exercise">
                  <span className="sr-only">Home</span>
                  <IconHome />
                </Link>
              ),
              key: 'dashboard',
            },
            { content: <span>Exercises</span>, key: 'exercise-toolbox' },
          ]}
          itemDivider={<span aria-hidden={true}>|</span>}
        />

        <div className="flex items-center justify-between mb-4 mt-2">
          <h1 className="h2 text-uppercase">Exercises</h1>
          <div className="flex items-center gap-2">
            <div className="flex rounded border border-gray-200 overflow-hidden">
              <button
                onClick={() => setViewMode('cards')}
                aria-label="Card view"
                className={cn(
                  'p-2 transition-colors',
                  viewMode === 'cards'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-surface-foreground hover:bg-gray-100',
                )}
              >
                <IconGrid />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={cn(
                  'p-2 transition-colors border-l border-gray-200',
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-surface-foreground hover:bg-gray-100',
                )}
              >
                <IconList />
              </button>
            </div>
            {returnToModuleUrl && (
              <Button
                icon={(props) => ArrowIcon({ ...props, className: cn(props.className, 'rotate-90 w-4 h-4') })}
                text="Return to Module"
                linkButton={{ url: returnToModuleUrl, id: 'return-to-module' }}
              />
            )}
          </div>
        </div>

        <div className="mb-6 max-w-2xl">
          <p className="mb-4">
            This is a library of short activities you can use any time. You don’t need a DAWN account to use these
            and you don’t have to do them in any particular order.
          </p>
          <p>
            You might find some of these useful to come back to when things are feeling tough. Others might help you
            to slow down, gather your thoughts, and work out some small steps you can take right now to improve your
            wellbeing. Pick whatever feels right for you in the moment.
          </p>
        </div>

        {renderExercises()}

        <section className="mb-8">
          <div className="bg-primary text-primary-foreground flex flex-col items-start px-4 py-3 rounded-t rounded-b mb-4">
            <h2 className="h5 m-0">Get support</h2>
            <p className="text-sm mt-1 m-0">
              A list of services and resources for when you need to reach out for more help.
            </p>
          </div>
          <div className="bg-secondary rounded p-4">
            <Button text="Get help and support" linkButton={{ url: '/get-help-and-support', id: 'get-support' }} />
          </div>
        </section>
      </Container>
    </PublicDashboardPage>
  );
};

export default ExerciseToolboxNextPage;
